import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Trophy, ChevronRight, CirclePlus, Calendar, RefreshCcw } from "lucide-react";

import { getTournament } from "../../../api/tournament.api";
import { listCompetitions } from "../../../api/competitions.api";
import Pagination from "../../../components/common/Pagination";

const TYPE_TONE = {
  INTERNATIONAL: "bg-button-primary/20 text-button-primary border-button-primary/40",
  DOMESTIC:      "bg-blue-500/15 text-blue-300 border-blue-400/30",
  LEAGUE:        "bg-amber-500/15 text-amber-300 border-amber-400/30",
  SCHOOL:        "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
  CLUB:          "bg-purple-500/15 text-purple-300 border-purple-400/30",
  LOCAL:         "bg-rose-500/15 text-rose-300 border-rose-400/30",
};

export default function TournamentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [comps, setComps] = useState(null);
  const [page, setPage] = useState(1);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    getTournament(id)
      .then((r) => { if (alive) setTournament(r.data); })
      .catch(() => { if (alive) setError("Couldn't load this tournament."); });
    return () => { alive = false; };
  }, [id]);

  useEffect(() => {
    let alive = true;
    listCompetitions(page, { tournament: id })
      .then((r) => { if (alive) setComps(r.data); })
      .catch(() => { if (alive) setError("Couldn't load competitions."); });
    return () => { alive = false; };
  }, [id, page]);

  const total = comps?.count ?? 0;
  const rows = comps?.results || [];

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md bg-bg-secondary border border-border-default text-text-primary hover:bg-bg-main transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button
          onClick={() => navigate("/cricket/tournaments")}
          className="inline-flex items-center gap-1 text-xs text-text-secondary hover:text-text-primary"
        >
          All tournaments
        </button>
      </div>

      {tournament ? (
        <div className="mb-5">
          <h2 className="text-xl font-bold">{tournament.name}</h2>
          <p className="text-sm text-text-secondary mt-0.5 flex items-center gap-3 flex-wrap">
            {tournament.short_name && (
              <span className="font-mono text-xs">{tournament.short_name}</span>
            )}
            {tournament.tournament_type && (
              <span className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border ${TYPE_TONE[tournament.tournament_type] || "bg-bg-secondary text-text-secondary border-border-default"}`}>
                {tournament.tournament_type}
              </span>
            )}
            {tournament.established_year && (
              <span>Est. {tournament.established_year}</span>
            )}
          </p>
        </div>
      ) : (
        <div className="text-text-secondary text-sm mb-5">Loading…</div>
      )}

      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm uppercase tracking-wider text-text-secondary">
          Competitions {total > 0 && <span className="text-text-primary font-medium">· {total}</span>}
        </h3>
        <Link
          to="/cricket/competitions/add"
          className="text-xs bg-button-primary hover:brightness-110 text-white px-3 py-1.5 rounded inline-flex items-center gap-1.5"
        >
          <CirclePlus className="w-3.5 h-3.5" /> Add competition
        </Link>
      </div>

      {error && (
        <div className="rounded border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-200 mb-4">
          {error}
        </div>
      )}

      {comps && rows.length === 0 && (
        <div className="rounded-2xl border border-border-default bg-bg-primary p-8 text-center">
          <Trophy className="w-8 h-8 text-text-secondary mx-auto mb-3 opacity-60" />
          <h3 className="text-base font-semibold text-text-primary mb-1">
            No competitions under this tournament yet
          </h3>
          <p className="text-sm text-text-secondary mb-5 max-w-md mx-auto">
            Create a competition and link it to this tournament to see it here.
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="rounded-lg border border-border-default overflow-hidden">
          {rows.map((c) => (
            <div
              key={c.id}
              className="flex items-center gap-3 px-4 py-3 hover:bg-bg-secondary/40 border-b border-border-default last:border-b-0"
            >
              <button
                onClick={() => navigate(`/cricket/competitions/${c.id}`)}
                className="flex-1 min-w-0 text-left"
              >
                <div className="text-sm font-medium text-text-primary truncate">{c.name}</div>
                <div className="text-[11px] text-text-secondary mt-0.5 flex items-center gap-2 flex-wrap">
                  {c.season && <span className="font-mono">{c.season}</span>}
                  {c.start_date && (
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> {c.start_date}{c.end_date ? ` → ${c.end_date}` : ""}
                    </span>
                  )}
                  <span className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border ${c.is_active ? "bg-emerald-500/15 text-emerald-300 border-emerald-400/30" : "bg-bg-secondary text-text-secondary border-border-default"}`}>
                    {c.is_active ? "Active" : "Archived"}
                  </span>
                </div>
              </button>
              <button
                onClick={() => navigate(`/cricket/competitions/${c.id}/sync`)}
                title="Map archival players to real profiles"
                className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border border-button-primary/40 text-button-primary hover:bg-button-primary/10 shrink-0"
              >
                <RefreshCcw className="w-3 h-3" /> Sync
              </button>
              <ChevronRight className="w-4 h-4 text-text-secondary shrink-0" />
            </div>
          ))}
        </div>
      )}

      <Pagination count={total} page={page} onPage={setPage} />
    </div>
  );
}
